"use client";

import { getCategoryCanonicalUrlV2 } from "api/category/getCategoryCanonicalUrlV2";
import { IUrlInformation } from "api/interfaces/get-page-details/common/nested-pagecontent.type";
import { IMakeTierActualResponse, IMakeTierPageContent } from "api/interfaces/get-page-details/tier-make-page.type";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "component/category/PagePlaceholder";
import { getTotalFilters } from "component/product/product-container-util";
import { LAYOUT } from "constant/layout-constant";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import PageContainer from "modules/components/PageContainer";
import PageStructureData from "modules/product/PageStructureData";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import CategorySection from "../../component/design-system/category-selection/CategorySection";
import CategorySlectionCardBig from "../../component/design-system/category-selection/CategorySlectionCardBig";
import usePrevious from "../../hoc/usePrevious";
import { equalVehicles } from "../../util/vehicle/equal-vehicles";
import MetaHead from "./common/MetaHead";
import PageHeader from "./common/PageHeader";
import RelatedProducts from "./common/RelatedProducts";
const SsrProductContainerCompact = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-compact" */"component/category/SsrProductContainerCompact"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const SsrProductContainerRegular = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-regular" */"component/category/SsrProductContainerRegular"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);

interface IProps {
  urlInformation: IUrlInformation;
  staticContent: IMakeTierPageContent;
  actualResponse: IMakeTierActualResponse;
  pageType: "TIER1_MAKE_PAGE" | "TIER2_MAKE_PAGE" | "TIER3_MAKE_PAGE"
}

function MakeTierPage(props: IProps) {
  const {
    actualResponse, urlInformation, staticContent, pageType,
  } = props;
  const { pageDetails, relatedProducts } = staticContent;
  const {
    productsAggregation, fitmentOptionsDetails, breadCrumbsV2,
    makeModelWithCategoryUrls, tiersWithUrls, makeModelWithCategoryUrlsV2,
  } = actualResponse;
  const { products: productList, ...filterOptions } = productsAggregation || {};
  const { make } = urlInformation;
  const { t } = useTranslation("category");

  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);
  const prevSelectedVehicle = usePrevious(selectedVehicle);

  const router = useRouter();
  const { query } = router;
  const { pathname, ...queryFilters } = query;

  useEffect(() => {
    if (!equalVehicles(prevSelectedVehicle, selectedVehicle)) {
      // user have selected new vehicle
      getCategoryCanonicalUrlV2(urlInformation, selectedVehicle, null).then((res: any) => {
        const { url, success, queryParams } = res.data;
        if (success) {
          const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            make: ma, model: mo, year: yr, ...buildQueryParams
          } = queryFilters;
          const { href, asPath } = getHrefAndPath({
            query: { ...queryParams, ...buildQueryParams }, asPath: url,
          });

          if (window.location.pathname !== url) {
            router.replace(href, asPath);
          }
        }
      });
    }
  }, [selectedVehicle]);

  const vehicle = { make };
  const makeModelsTierLinks = makeModelWithCategoryUrlsV2 && makeModelWithCategoryUrlsV2.length > 0 ? makeModelWithCategoryUrlsV2 : makeModelWithCategoryUrls;
  const [isMobile] = useScreenSize();

  const url = "/[pathname]";
  const appliedFilters = getTotalFilters(
    t,
    query,
    {},
    selectedVehicle,
    {},
  );
  const { pageDisplayableTitle = "" } = staticContent.pageDetails || {};

  const propsToPass = {
    fitmentOptionsDetails,
    defaultLayout: LAYOUT.COMPACT,
    productList,
    filterOptions,
    url,
    pageType,
    appliedFilters,
    staticContent,
    pageDisplayableTitle,
    filterParams: {
    },
    tiersWithUrls,
    categoryAggregation: productsAggregation?.categoryAggregation,
  };

  return (
    <PageContainer>

      <MetaHead {...{ ...staticContent.metaData, ...staticContent.pageDetails, pathname }} />

      <PageHeader {...{
        vehicle, pageDetails, staticContent, breadCrumbsV2,
      }}
      />

      <CategorySection
        title={t("Popular Make Models Category")}
        {...{
          categoryUrls: makeModelsTierLinks,
        }}
      >

        <CategorySlectionCardBig
          imageProps={{ width: 200, height: 200 }}
        />
      </CategorySection>
      <PageStructureData
        pageStructure={staticContent.pageStructure}
        title="Overview"
      />
      <HideIf show={productsAggregation}>
        <div id="PRODUCTS" className="row">
          {(isMobile) ? (<SsrProductContainerCompact {...propsToPass} />)
            : (
              <div className="col-xs-12">
                <SsrProductContainerRegular {...propsToPass} />
              </div>
            )}
        </div>
      </HideIf>

      <RelatedProducts products={relatedProducts} />
    </PageContainer>
  );
}

export default MakeTierPage;
